import { Component, inject, Input, OnInit, ViewChild, ViewEncapsulation } from '@angular/core';
import { DiaryConfigModalComponent } from "../diary-config-modal/diary-config-modal.component";
import { AlertService } from "../../../../shared/alert/alert.service";
import { LogHelper } from "../../../../core/helpers/log.helper";
import { DateHelper } from "../../../../core/helpers/date-helper";
import { FormControl, FormGroup, Validators } from "@angular/forms";
import moment from "moment";
import { TimezoneSelectComponent } from "../../../../shared/timezone-select/timezone-select.component";
import { environment } from "../../../../../environments/environment";
import { TimezonesGMT } from "../../../../core/constants/timezones-gmt";
import { EDiaryService } from "../../../../core/services/e-diary.service";
import { PatientEdiaryEntry } from "../../../../core/models/patient-ediary-entry.model";
import { PatientEdiaryConfig } from "../../../../core/models/patient-ediary-config.model";
import { ModalV2Component } from "../../../../shared/modal-v2/modal-v2.component";
import { ModalComponent } from "../../../../shared/modal/modal.component";
import { ConfirmActionModalComponent } from "../../../../shared/confirm-action-modal/confirm-action-modal.component";
import { AuthService } from "../../../../core/services/auth.service";
import { RoleNames } from "../../../../core/constants/role-names";
import { Permissions } from "../../../../core/constants/permissions";
import { DiaryFormService } from "../../../../core/services/diary-form.service";
import { UpdatePatientEdiaryEntriesRequest } from "../../../../core/models/trial-diary-form/ediary.models";
import { DiaryFormSummary } from "../../../../core/models/trial-diary-form/diary-form-summary.model";
import { DiaryType } from "../../../../core/constants/diary-form-type.enum";
import { SplitButtonAction } from "../../../../shared/split-button/split-button.component";

@Component({
  selector: 'app-patient-ediaries',
  templateUrl: './patient-ediaries.component.html',
  styleUrl: './patient-ediaries.component.scss',
  encapsulation: ViewEncapsulation.None
})
export class PatientEdiariesComponent implements OnInit{
  @ViewChild('timezoneSelect') timezoneSelect: TimezoneSelectComponent;
  @ViewChild('diaryConfigModal') diaryConfigModal: DiaryConfigModalComponent;
  @ViewChild('ediaryEntryModal') ediaryEntryModal: ModalV2Component;
  @ViewChild('reminderModal') reminderModal: ModalComponent;
  @ViewChild('confirmDeleteModal') confirmDeleteModal: ConfirmActionModalComponent;

  @Input('patientTrialId') patientTrialId: string;
  @Input('trialId') trialId: string;

  private readonly _ediaryService = inject(EDiaryService);
  private readonly _alertService = inject(AlertService);
  private readonly _diaryFormService = inject(DiaryFormService);
  protected readonly authService = inject(AuthService);
  protected readonly environment = environment;

  entries: PatientEdiaryEntry[] = [];
  hasSubmissions = false;
  diaryConfig: PatientEdiaryConfig = null;
  diaries: DiaryFormSummary[] = [];
  selectedDiaryId: string | null = null;
  isOnboardingQuestions = false;
  isIntraDay = false;
  allTimezones = TimezonesGMT.all();
  isDevelopmentEnv: boolean = false;
  entryToDelete: PatientEdiaryEntry = null;

  simulateActions: SplitButtonAction[] = [
    { label: 'Simulate Next Day', value: 'day', variant: 'outline', default: true },
    { label: 'Simulate Next Week', value: 'week', variant: 'outline', dropdownVariant: 'danger' },
    { label: 'Simulate Next Month', value: 'month', variant: 'outline', dropdownVariant: 'danger' },
  ];

  reminderForm = new FormGroup({
    isBusy: new FormControl(false),
    startDate: new FormControl<Date>(null, Validators.required),
    minDate: new FormControl<Date>(null, Validators.required),
    reminderTime: new FormControl(''),
    reminderTimeUtc: new FormControl(''),
    timezone: new FormControl('', Validators.required),
    timezoneLabel: new FormControl(''),
  });

  ngOnInit() {
    this.loadDiaries();

    if (environment.environment === "DEVELOPMENT" || environment.environment === "STAGING" || environment.environment === "VALIDATION")
      this.isDevelopmentEnv = true;
  }

  loadDiaries() {
    this._diaryFormService.list(this.trialId).subscribe({
      next: diaries => {
        this.diaries = diaries.filter(d => d.enabled);
        if (this.diaries.length > 0) {
          this.selectedDiaryId = this.diaries[0].id;
        }
        this.updateDiaryTypeFlags();
        this.loadEdiaryConfig();
        this.loadEntries();
      },
      error: err => {
        LogHelper.log(err);
        this._alertService.showErrorAlert(err);
      }
    });
  }

  selectDiary(id: string) {
    this.selectedDiaryId = id;
    this.updateDiaryTypeFlags();
    this.loadEdiaryConfig();
    this.loadEntries();
  }

  private updateDiaryTypeFlags() {
    const diary = this.diaries.find(d => d.id === this.selectedDiaryId);
    this.isOnboardingQuestions = diary?.diaryType === DiaryType.OnboardingQuestions;
    this.isIntraDay = diary?.diaryType === DiaryType.IntraDay;
  }

  getDiaryDisplayName(diary: DiaryFormSummary): string {
    return diary.name?.trim() || 'Default Diary';
  }

  loadEntries() {
    if (!this.selectedDiaryId) return;
    this._ediaryService.getEdiaryEntries(this.patientTrialId, this.selectedDiaryId).subscribe({
      next: entries => {
        this.entries = entries;

        this.hasSubmissions = entries.filter(e => e.submittedDate).length > 0
      },
      error: error => {
        LogHelper.log(error);
        this._alertService.showErrorAlert(error);
      }
    });
  }

  loadEdiaryConfig(): void {
    if (!this.selectedDiaryId) return;
    this._ediaryService.getEdiaryConfig(this.patientTrialId, this.selectedDiaryId).subscribe({
      next: config => {
        this.diaryConfig = config;
      },
      error: (error) => {
        LogHelper.log(error);
        this._alertService.showErrorAlert(error);
      }
    })
  }

  shouldShowEditEntryButton(entry: PatientEdiaryEntry): boolean {
    if (this.authService.hasPermission(Permissions.PatientEdiaryModify) && this.authService.isEndpointDataEditorForTrial(this.trialId))
      return true;

    return false;
  }

  /**
   * When the timezone is changed we need to get the full text label of the selected timezone to show in the disabled
   * timezone input
   * @param option
   */
  onTimezoneChanged(option: { value: string, text: string }) {
    if (option)
      this.reminderForm.patchValue({timezoneLabel: option.text});
  }

  shouldShowUtcTime(sourceControl: string): boolean {
    let time = this.reminderForm.get(sourceControl).value;
    let timezoneStr = this.reminderForm.get('timezone').value;

    return time !== '' && timezoneStr !== null && timezoneStr !== '';
  }

  shouldShowAmendRemindersButton(): boolean {
    if (this.diaryConfig === null)
      return false;

    if (!this.diaryConfig.remindersEnabled && !this.diaryConfig.emailRemindersEnabled)
      return false;

    const selectedDiary = this.diaries.find(d => d.id === this.selectedDiaryId);
    if (selectedDiary?.diaryType === DiaryType.Periodic)
    {
      // If today is after the diary end date - hide the button
      const today = new Date();
      if (this.diaryConfig.remindersEnabled && today > new Date(this.diaryConfig.endDate))
        return false;

      if (!this.entries || this.entries.length === 0)
        return false;

      // If the last entry has been submitted - hide the button
      const lastEntry = this.entries[this.entries.length - 1];
      if (lastEntry.submittedDate)
        return false;
    }

    return true;
  }

  onShowAmendRemindersModal() {
    let startDate = new Date(this.diaryConfig.startDate);
    let today = new Date();

    if (today > startDate)
      startDate = today;

    let submittedEntries = this.entries.filter(entry => entry.submittedDate);
    if (submittedEntries.length > 0) {
      const lastSubmittedEntry = submittedEntries[submittedEntries.length - 1];
      startDate = DateHelper.addDaysToDate(lastSubmittedEntry.date, 1);
    }

    // If the date is today, we need to add one day
    // For development we want to allow the user to change the current day for testing purposes
    if (!this.isDevelopmentEnv && startDate <= today)
      startDate = DateHelper.addDaysToDate(today, 1);

    if (this.diaryConfig.remindersEnabled) {
      this.reminderForm.get('reminderTime').setValidators(Validators.required);
      this.reminderForm.get('reminderTimeUtc').setValidators(Validators.required);
    } else {
      this.reminderForm.get('reminderTime').setValidators(null);
      this.reminderForm.get('reminderTimeUtc').setValidators(null);
    }

    this.reminderForm.get('startDate').setValidators(Validators.required);
    this.reminderForm.get('minDate').setValidators(Validators.required);

    this.reminderForm.get('reminderTime').updateValueAndValidity();
    this.reminderForm.get('reminderTimeUtc').updateValueAndValidity();
    this.reminderForm.get('startDate').updateValueAndValidity();
    this.reminderForm.get('minDate').updateValueAndValidity();

    this.reminderForm.patchValue({
      startDate: startDate,
      minDate: startDate,
      reminderTime: DateHelper.convertMinutesTo24HourTime(this.diaryConfig.reminderTimeMinutes),
      reminderTimeUtc: DateHelper.convertMinutesTo24HourTime(this.diaryConfig.reminderTimeMinutesUtc),
      timezone: this.diaryConfig.timezone
    });

    this.timezoneSelect.setTimezone(this.diaryConfig.timezone);

    this.reminderForm.get('reminderTime').valueChanges.subscribe(value => {
      this.updateTimeUtc('reminderTime', 'reminderTimeUtc', this.reminderForm);
    });

    this.reminderModal.show();
  }

  getMinutesExpressedAsTime(time: number): string {
    if (time === null)
      return '-';

    return DateHelper.convertMinutesTo24HourTime(time);
  }

  formatSubmittedDateTime(entry: PatientEdiaryEntry): string {
    if (!entry.submittedDate) return '-';

    let dateStr = entry.submittedDate.toString();
    if (!dateStr.endsWith('Z')) {
      dateStr += 'Z';
    }
    
    const date = new Date(dateStr);
    const formatter = new Intl.DateTimeFormat('en-GB', {
      timeZone: entry.timezone,
      day: 'numeric',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      hour12: false
    });

    return formatter.format(date);
  }

  getTimezoneWithOffset(timezone: string) {
    let t = this.allTimezones.find(a => a.zone === timezone);
    if (!t)
      return '-';

    return t.gmt + ' ' + t.name;
  }

  updateTimeUtc(sourceControl: string, targetControl: string, form: FormGroup) {
    let time = form.get(sourceControl).value;
    let timezoneStr = form.get('timezone').value;

    if (time === null || time === '' || timezoneStr === null || timezoneStr === '') {
      form.patchValue({ targetControl: '' });
      return;
    }

    let timezone = form.get('timezone').value;
    if (timezone === null || timezone === '')
      return;

    form.get(targetControl).setValue(this.convertToUTC(time, timezone));
  }

  convertToUTC(time: string, timezone: string): string {
    // Parse the input time and timezone
    const timeInZone = moment.tz(time, "HH:mm", timezone);

    // Convert to UTC and format it as a 24-hour string
    const timeInUTC = timeInZone.utc().format("HH:mm");

    return timeInUTC;
  }

  onDiaryConfigChanged() {
    this.loadEntries();
    this.loadEdiaryConfig();
  }

  onDiaryEntrySaved() {
    this.loadEntries();
  }

  onShowDeleteModal(entry: PatientEdiaryEntry) {
    this.entryToDelete = entry;
    const formattedDate = entry?.date ? new Date(entry.date).toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' }) : '';
    const message = formattedDate
      ? `Are you sure you want to remove the e-diary entry dated ${formattedDate}?`
      : 'Are you sure you want to remove this e-diary entry?';
    this.confirmDeleteModal.showConfirmation(entry.id, 'Remove E-Diary Entry', message, 'Remove', 'Cancel');
  }

  onCancelDelete() {
    this.entryToDelete = null;
    this.confirmDeleteModal.hide();
  }

  onConfirmDelete(entryId: string) {
    if (!entryId) {
      return;
    }

    this._ediaryService.deleteEdiaryEntry(this.patientTrialId, entryId).subscribe({
      next: () => {
        this._alertService.showSuccessAlert('E-diary entry removed');
        this.entryToDelete = null;
        this.loadEntries();
      },
      error: err => {
        LogHelper.log(err);
        this._alertService.showErrorAlert(err);
      }
    });
  }

  onSimulateSplitAction(value: string) {
    switch (value) {
      case 'day':
        this.onSimulateNextDay();
        break;
      case 'week':
        this.onSimulateNextWeek();
        break;
      case 'month':
        this.onSimulateNextMonth();
        break;
    }
  }

  onSimulateNextDay() {
    this._ediaryService.simulateConditionalNextDay(this.patientTrialId, this.selectedDiaryId).subscribe({
      next: (res: any) => {
        if (res?.completed) {
          this._alertService.showSuccessAlert('E-Diary completed, no more entries allowed.');
        } else {
          this._alertService.showSuccessAlert('Simulated next day — new entry created');
        }
        this.loadEntries();
        this.loadEdiaryConfig();
      },
      error: err => {
        LogHelper.log(err);
        this._alertService.showErrorAlert(err);
      }
    });
  }

  onSimulateNextWeek() {
    this._ediaryService.simulateNextWeek(this.patientTrialId, this.selectedDiaryId).subscribe({
      next: (res: any) => {
        if (res?.completed) {
          this._alertService.showSuccessAlert('E-Diary completed, no more entries allowed.');
        } else {
          this._alertService.showSuccessAlert('Simulated next week — entries shifted back 7 days');
        }
        this.loadEntries();
        this.loadEdiaryConfig();
      },
      error: err => {
        LogHelper.log(err);
        this._alertService.showErrorAlert(err);
      }
    });
  }

  onSimulateNextMonth() {
    this._ediaryService.simulateNextMonth(this.patientTrialId, this.selectedDiaryId).subscribe({
      next: (res: any) => {
        if (res?.completed) {
          this._alertService.showSuccessAlert('E-Diary completed, no more entries allowed.');
        } else {
          this._alertService.showSuccessAlert('Simulated next month — entries shifted back 30 days');
        }
        this.loadEntries();
        this.loadEdiaryConfig();
      },
      error: err => {
        LogHelper.log(err);
        this._alertService.showErrorAlert(err);
      }
    });
  }

  onResetDiary() {
    this._ediaryService.resetPatientEdiary(this.patientTrialId, this.selectedDiaryId).subscribe({
      next: rsp => {
        this._alertService.showSuccessAlert("E-diary entries and settings have been reset");
        this.loadEntries();
      },
      error: err => {
        this._alertService.showErrorAlert(err);
      }
    });
  }

  onReminderFormSubmit() {
    if (!this.reminderForm.valid)
      return;

    const startDate = this.reminderForm.get('startDate').value;
    const minDate = this.reminderForm.get('minDate').value;

    // If startDate is before minDate, return an error as an observable
    if (startDate < minDate) {
      this._alertService.showWarningAlert('Date cannot be before ' + this.reminderForm.get('minDate').value);
      return;
    }

    if (new Date(startDate) > new Date(this.diaryConfig.endDate)) {
      this._alertService.showWarningAlert('Date cannot be after the dosage end date');
      return;
    }

    this.reminderForm.patchValue({ isBusy: true });

    const request: UpdatePatientEdiaryEntriesRequest = {
      configId: this.diaryConfig.id,
      reminderTimeMinutes: DateHelper.convert24HourTimeToMinutes(this.reminderForm.get('reminderTime').value),
      reminderTimeMinutesUtc: DateHelper.convert24HourTimeToMinutes(this.reminderForm.get('reminderTimeUtc').value),
      timezone: this.reminderForm.get('timezone').value,
      from: DateHelper.dateToYyyyMmDd(startDate)
    };

    this._ediaryService.updatePatientEdiaryEntries(this.patientTrialId, request).subscribe({
      next: rsp => {
        this.reminderForm.patchValue({ isBusy: false });
        this.loadEntries();
        this.loadEdiaryConfig();
        this.reminderModal.hide();
      },
      error: error => {
        LogHelper.log(error);
        this._alertService.showErrorAlert(error);
        this.reminderForm.patchValue({ isBusy: false });
      }
    });
  }

  protected readonly RoleNames = RoleNames;
  protected readonly Permissions = Permissions;
}
